// Joint sets: the set cards (statistics, window editor, per-set φ), automatic
// classification (DSE) and the set exports. GPL-3.0-or-later.
import * as S from "@dipfield/stereo";
import { t, tn } from "../../i18n/index.js";
import { $, esc, download, num } from "../../lib/dom.js";
import { fmt, fmtPlane, fmtName } from "../../lib/format.js";
import { setColor } from "../../lib/theme.js";
import { toast } from "../../lib/toast.js";
import * as J from "../../lib/jobs.js";
import { pushHistory, undo, touched } from "../../state/store.js";
import { applyDse, editWindow } from "../../state/ops.js";
import { dseArgs, computeDse, weighted, type DseResult } from "../../state/derived.js";
import { APP, ENGINES } from "../../state/engines.js";
import { X, P, on, update, draw, onRender } from "./ctx.js";

function windowEditor(w: S.SetWindow, k: number) {
  const f = (key: string, label: string, v: number) =>
    `<label class="field" style="margin:0"><span>${label}</span><input data-wk="${k}" data-wf="${key}" type="number" step="any" value="${+v.toFixed(1)}"></label>`;
  if (w.kind === "circle") { const l = S.vecToLine(w.axis);
    return `<div class="wedit">${f("trend", t("Centre trend"), l.trend)}${f("plunge", t("plunge"), l.plunge)}${f("radius", t("Radius (°)"), w.radius)}</div>`; }
  if (w.kind === "curved") return `<div class="wedit">${f("t1", t("Trend from"), w.trendStart)}${f("t2", t("to"), S.wrap360(w.trendStart + w.trendWidth))}
      ${f("p1", t("Plunge min"), w.plungeMin)}${f("p2", t("max"), w.plungeMax)}</div><p class="muted small" style="margin:.2rem 0 0">${t("A negative plunge includes poles across the rim")}</p>`;
  return `<p class="muted small" style="margin:.3rem 0 0">${t("Freehand window — drag on the net to move it")}</p>`;
}
/** A 44 px stereonet: the set's window and its mean pole, in its own colour. */
function setThumb(k: number): string {
  const p = P(), set = p.sets[k], col = setColor(k), sm = X.d.summaries[k], c = 22, r = 19, proj = p.stereo.projection;
  const T = (q: S.XY) => `${(c + q.x * r).toFixed(1)},${(c - q.y * r).toFixed(1)}`;
  let d = "";
  for (const line of S.windowOutline(set.window, proj)) d += line.map((q, i) => (i ? "L" : "M") + T(q)).join("");
  let mean = "";
  if (sm?.stats) { const m = S.project(sm.stats.mean, proj); mean = `<circle cx="${(c + m.x * r).toFixed(1)}" cy="${(c - m.y * r).toFixed(1)}" r="2.6" fill="${col}"/>`; }
  return `<span class="sthumb" aria-hidden="true"><svg viewBox="0 0 44 44"><defs><clipPath id="thc${k}"><circle cx="${c}" cy="${c}" r="${r}"/></clipPath></defs>
    <circle cx="${c}" cy="${c}" r="${r}" fill="var(--rail)" stroke="var(--rule)" stroke-width="1"/>
    <path d="M${c} 3v38M3 ${c}h38" stroke="var(--hair)" stroke-width=".8"/>
    <g clip-path="url(#thc${k})"><path d="${d}" fill="${col}" fill-opacity=".18" stroke="${col}" stroke-width="1.3"/></g>${mean}</svg></span>`;
}
export function renderSets() {
  const el = $("setlist"), p = P(), f = p.data.format;
  if (X.selectedSet >= p.sets.length) X.selectedSet = -1;
  if (!p.sets.length) {
    el.innerHTML = `<div class="hint"><b>${t("No joint sets yet")}</b>
      <ol><li>${t("Press <b>Classify sets automatically (DSE)</b> above and review the result, or")}</li>
      <li>${t("pick <b>Circle</b>, <b>Curved</b> or <b>Freehand</b> and draw around a cluster of poles on the net")}</li></ol>
      <p class="why">${t("Once sets are defined you get mean planes, Fisher K and the 95% cone; the Slope and Tunnel modules use them.")}</p></div>`;
    $("set-export").hidden = true; return;
  }
  $("set-export").hidden = false;
  const unassigned = X.d.assignment.reduce((a, v) => a + (v === 0 ? 1 : 0), 0);
  const phiGlobal = p.slope.kinematics.friction;
  el.innerHTML = p.sets.map((set, k) => {
    const sm = X.d.summaries[k], s2 = sm?.stats;
    const mp = sm?.meanPlane ? fmtPlane(sm.meanPlane.dip, sm.meanPlane.dipDir, f) : "–";
    return `<div class="setcard${k === X.selectedSet ? " sel" : ""}" data-k="${k}" style="--c:${setColor(k)}">
      <div class="setrow">${setThumb(k)}<span class="sw" style="background:${setColor(k)}"></span>
        <input class="setname" data-rename="${k}" value="${esc(set.name)}" aria-label="${t("Set name")}">
        <span class="muted small">${tn(sm?.members.length ?? 0, "{n} pole", "{n} poles")}</span>
        <button class="icon" data-delset="${k}" aria-label="${t("Delete {name}", { name: esc(set.name) })}" title="${t("Delete set")}">×</button></div>
      <dl class="setstats"><dt>${t("Mean plane")} (${fmtName(f)})</dt><dd>${mp}</dd>
        <dt>Fisher K</dt><dd>${s2?.K == null ? "–" : Number.isFinite(s2.K) ? fmt(s2.K, 1) : "∞"}</dd>
        <dt>${t("Cone 95%")}</dt><dd>${s2?.confidenceDeg == null ? "–" : fmt(s2.confidenceDeg, 2) + "°"}</dd>
        <dt>${t("Variability 1σ")}</dt><dd>${s2?.variability1Deg == null ? "–" : fmt(s2.variability1Deg, 1) + "°"}</dd>
        <dt>${t("φ of the set")}</dt><dd><input class="phiin" data-phi="${k}" type="number" min="0" max="89" step="1" placeholder="${fmt(phiGlobal, 0)}" value="${set.friction ?? ""}" aria-label="${t("Friction angle of {name}", { name: esc(set.name) })}" title="${t("Empty = the global value")}"></dd></dl>
      ${k === X.selectedSet ? windowEditor(set.window, k) : ""}</div>`;
  }).join("") + `<p class="muted small">${tn(unassigned, "{n} pole is in no set", "{n} poles are in no set")} · ${t("a pole inside several windows goes to the set whose centre is nearest")}</p>`;
}

/** The sets in the DipField exchange format (schemaVersion 1), with the engine versions. */
function setExchange() {
  const p = P();
  return JSON.stringify({ schemaVersion: 1, source: "dipfield-suite", engine: "manual-windows", producedBy: { ...APP, engines: ENGINES },
    weighting: weighted(p) ? { method: "terzaghi", traverses: p.data.traverses.map((tv) => ({ name: tv.name, ...tv.tr })), minBiasDeg: p.data.terzaghi.minBiasDeg } : null,
    sets: p.sets.map((set, k) => { const sm = X.d.summaries[k], s2 = sm?.stats;
      return { id: set.name, meanDip: sm?.meanPlane?.dip ?? null, meanDipDir: sm?.meanPlane?.dipDir ?? null, n: sm?.members.length ?? 0,
        fisherK: s2?.K != null && Number.isFinite(s2.K) ? s2.K : null,
        cone: { confidence: s2?.confidenceDeg ?? null, confidenceLevel: s2?.level ?? 0.95, variability1sd: s2?.variability1Deg ?? null },
        window: set.window }; }) }, null, 1);
}
function setCSV() {
  const p = P(), rows = [["set", "n", "dip", "dip_direction", "strike_rhr", "fisher_k", "cone95_deg", "variability_1sd_deg"]];
  p.sets.forEach((set, k) => { const sm = X.d.summaries[k], s2 = sm?.stats, mp = sm?.meanPlane;
    rows.push([set.name.replace(/[,\n]/g, " "), String(sm?.members.length ?? 0), mp ? mp.dip.toFixed(1) : "", mp ? mp.dipDir.toFixed(1) : "",
      mp ? S.dipDirToStrikeRHR(mp.dipDir).toFixed(1) : "", s2?.K != null && Number.isFinite(s2.K) ? s2.K.toFixed(2) : "",
      s2?.confidenceDeg != null ? s2.confidenceDeg.toFixed(2) : "", s2?.variability1Deg != null ? s2.variability1Deg.toFixed(2) : ""]); });
  return rows.map((r) => r.join(",")).join("\n") + "\n";
}

function readAuto() {
  const n = (id: string, lo: number, hi: number, def: number) => num($<HTMLInputElement>(id), lo, hi, def);
  const o = P().stereo.dse;
  o.projection = $<HTMLSelectElement>("au-proj").value === "Equal-angle" ? "Equal-angle" : "Equal-area";
  o.maxSets = Math.round(n("au-max", 1, 12, 5)); o.minAngleDeg = n("au-sep", 5, 60, 30); o.coneDeg = n("au-cone", 5, 60, 30); o.minPeakPct = n("au-peak", 0, 50, 5);
  o.useWeights = $<HTMLInputElement>("au-w").checked;
}
function syncAuto() {
  const o = P().stereo.dse;
  $<HTMLSelectElement>("au-proj").value = o.projection; $<HTMLInputElement>("au-max").value = String(o.maxSets);
  $<HTMLInputElement>("au-sep").value = String(o.minAngleDeg); $<HTMLInputElement>("au-cone").value = String(o.coneDeg);
  $<HTMLInputElement>("au-peak").value = String(o.minPeakPct); $<HTMLInputElement>("au-w").checked = o.useWeights;
}

export function mountSets() {
  onRender(() => renderSets());
  syncAuto();
  const list = $("setlist");
  on(list, "change", (e: Event) => {
    const el = e.target as HTMLInputElement, p = P();
    if (el.dataset.phi != null) {
      const k = +el.dataset.phi, v = el.value.trim() === "" ? null : Number(el.value);
      pushHistory(); p.sets[k].friction = v != null && Number.isFinite(v) ? Math.max(0, Math.min(89, v)) : null; update(false); return;
    }
    if (el.dataset.wk != null) {
      const v = Number(el.value);
      if (!Number.isFinite(v)) { renderSets(); return; }
      pushHistory(); editWindow(p.sets[+el.dataset.wk], el.dataset.wf!, v); update(false); return;
    }
    if (el.dataset.rename == null) return;
    const name = el.value.trim(); if (!name) { renderSets(); return; }
    pushHistory(); p.sets[+el.dataset.rename].name = name.slice(0, 40); update(false);
  });
  on(list, "click", (e: Event) => {
    const tg = e.target as HTMLElement;
    const b = tg.closest("[data-delset]") as HTMLElement | null;
    if (b) { pushHistory(); P().sets.splice(+b.dataset.delset!, 1); X.selectedSet = -1; update(false); return; }
    const card = tg.closest(".setcard") as HTMLElement | null;
    if (card && !tg.closest("input")) { const k = +card.dataset.k!; X.selectedSet = X.selectedSet === k ? -1 : k; renderSets(); draw(); }
  });
  on(list, "mouseover", (e: Event) => { const c = (e.target as HTMLElement).closest<HTMLElement>(".setcard"); const k = c ? +c.dataset.k! : -1; if (k !== X.hoverSet) { X.hoverSet = k; draw(); } });
  on(list, "mouseleave", () => { if (X.hoverSet >= 0) { X.hoverSet = -1; draw(); } });
  on($("sets-clear"), "click", () => {
    const n = P().sets.length; if (!n) return;
    pushHistory(); P().sets = []; X.selectedSet = -1; update(false); toast(tn(n, "Deleted {n} set", "Deleted {n} sets"), undo);
  });
  on($("set-json"), "click", () => download("dipfield-sets.json", setExchange(), "application/json"));
  on($("set-csv"), "click", () => download("dipfield-sets.csv", setCSV(), "text/csv"));
  ["au-proj", "au-max", "au-sep", "au-cone", "au-peak", "au-w"].forEach((id) => on($(id), "change", () => { readAuto(); touched(); }));

  on($("au-run"), "click", () => {
    const p = P();
    if (p.data.planes.length < 10) { toast(t("At least 10 rows are needed")); return; }
    readAuto();
    const btn = $<HTMLButtonElement>("au-run"), label = btn.textContent;
    btn.disabled = true; btn.textContent = t("Computing…");
    const args = dseArgs(p, X.d), cone = p.stereo.dse.coneDeg;
    J.run<DseResult>("dse", args, () => computeDse(args)).then((r) => {
      if (X.signal.aborted) return;
      try {
        if (!r.poles.length) { toast(t("No cluster stands out enough; try a lower minimum peak")); return; }
        pushHistory(); applyDse(P(), r, cone); X.selectedSet = -1; update(false);
        toast(tn(r.poles.length, "Found {n} set · review and adjust the windows with the Edit tool", "Found {n} sets · review and adjust the windows with the Edit tool"), undo);
      } finally { btn.disabled = false; btn.textContent = label; }
    });
  });
}
